import React from "react";
import {
  AccessTime,
  AttachFile,
  DirectionsCarFilledOutlined,
  HouseOutlined,
  InfoOutlined,
  LineWeight,
  LocalShippingOutlined,
  LocationOnOutlined,
  PersonOutlineOutlined,
  PhoneAndroidOutlined,
  PriorityHighOutlined,
  ProductionQuantityLimits,
  SourceOutlined,
  Warehouse,
} from "@mui/icons-material";
import { Box, Chip, Grid, Typography } from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import Status from "components/Shared/Status";

import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { useAuth } from "hooks";

import { localizeDateTimeShort } from "utils/dateFormats";
import { formatTimeRemaining } from "utils/holdDuration";
import returnImage from "../../../../../assets/statusImages/return.png";
import deliveredImage from "../../../../../assets/statusImages/delivered.png";
import cancelledImage from "../../../../../assets/statusImages/cancelled.png";
import onHoldImage from "../../../../../assets/statusImages/onhold.png";
import QRCode from "react-qr-code";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import FileIcon from "components/Shared/FileIcon";

const getSecurityStyle = (type) => {
  switch (type) {
    case "ID":
      return { backgroundColor: "#2196f3", textColor: "#fff" };
    case "Security Code":
      return { backgroundColor: "#A05F00", textColor: "#fff" };
    case "OTP":
      return { backgroundColor: "#f44336", textColor: "#fff" };
    default:
      return { backgroundColor: "#3E3E42", textColor: "#fff" };
  }
};

const statusStyles = {
  Pending: { backgroundColor: "#F8C471", textColor: "#D35400" }, // Orange shades
  Delivered: { backgroundColor: "#DFF7DF", textColor: "green" }, // Green shades
  Return: { backgroundColor: "#FADBD8", textColor: "#C0392B" }, // Red shades
  Cancelled: { backgroundColor: "#FADBD8", textColor: "#C0392B" }, // Red shades
  Collected: { backgroundColor: "#EBF5FB", textColor: "#3498DB" }, // Light blue
  Loaded: { backgroundColor: "#EBF5FB", textColor: "#3498DB" }, // Light blue
  Unloaded: { backgroundColor: "#EBF5FB", textColor: "#3498DB" }, // Light blue
  CrossDocked: { backgroundColor: "#EBF5FB", textColor: "#3498DB" }, // Light blue
  SendOut: { backgroundColor: "#EBF5FB", textColor: "#3498DB" }, // Light blue
  Warehousing: { backgroundColor: "#EBF5FB", textColor: "#3498DB" }, // Light blue
  DeliveryFailed: { backgroundColor: "#FADBD8", textColor: "#C0392B" }, // Red shades
  Unpaid: { backgroundColor: "#c70020", textColor: "#FFFFFF" }, // Red
  Uncollected: { backgroundColor: "#c70020", textColor: "#FFFFFF" }, // Red
  "Partially Paid": { backgroundColor: "#e4e500", textColor: "#000000" }, // Yellow
  "Fully Paid": { backgroundColor: "#008000", textColor: "#FFFFFF" }, // Green
  "Fully Collected": { backgroundColor: "#008000", textColor: "#FFFFFF" }, // Green
  default: { backgroundColor: "#808080", textColor: "#FFFFFF" }, // Gray
};

const statusImages = {
  Delivered: deliveredImage,
  Cancelled: cancelledImage,
};

const getStatusStyle = (status) => statusStyles[status] || statusStyles.default;

const ShipmentBasicDetail = () => {
  const { isCustomer, isVendor, isContractor, isDriver, isVendorStaff } = useAuth();
  const canGoToLink = isCustomer || isVendor || isContractor || isDriver || isVendorStaff;
  const { ShipmentDetail, loadingShipment } = useShipmentDetailContext();

  // Re-render every 30s so the "time remaining" label stays fresh.
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!ShipmentDetail?.isOnHold) return;
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, [ShipmentDetail?.isOnHold]);

  return !loadingShipment ? (
    <Box padding={3}>
      <Grid container spacing={4}>
        <Grid item md={10}>
          <Box display="flex" flexDirection="column" gap={3}>
            <Grid container spacing={2}>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <LocalShippingOutlined color="primary" />}
                  title="Shipment ID"
                  subtitle={ShipmentDetail?.shipmentId
                    ?.replace(/(.{4})/g, "$1-")
                    .slice(0, -1)}
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <AccessTime color="primary" />}
                  title="Shipment Created Date"
                  subtitle={
                    ShipmentDetail?.createdAt
                      ? localizeDateTimeShort(ShipmentDetail?.createdAt)
                      : "N/A"
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <DirectionsCarFilledOutlined color="primary" />}
                  title={
                    ShipmentDetail?.vendor
                      ? "Vendor Profile"
                      : "Customer Profile"
                  }
                  subtitle={
                    ShipmentDetail?.vendor &&
                      typeof ShipmentDetail.vendor === "object" ? (
                      canGoToLink ? (
                        ShipmentDetail.vendor.shopName || "N/A"
                      ) : (
                        <OpenInNewTab
                          label={ShipmentDetail.vendor.shopName || "N/A"}
                          link={`/app/crm/vendor-profile/${ShipmentDetail.vendor._id}/detail`}
                        />
                      )
                    ) : ShipmentDetail?.customerId &&
                      typeof ShipmentDetail.customerId === "object" ? (
                      canGoToLink ? (
                        ShipmentDetail.customerId.name ||
                        ShipmentDetail.customerId.customerUser?.name ||
                        "N/A"
                      ) : (
                        <OpenInNewTab
                          label={
                            ShipmentDetail.customerId.name ||
                            ShipmentDetail.customerId.customerUser?.name ||
                            "N/A"
                          }
                          link={`/app/crm/customerProfile/${ShipmentDetail.customerId._id}/detail`}
                        />
                      )
                    ) : (
                      "N/A"
                    )
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <PersonOutlineOutlined color="primary" />}
                  title="Receiver"
                  subtitle={
                    ShipmentDetail?.receiver &&
                      typeof ShipmentDetail.receiver === "object" ? (
                      canGoToLink ? (
                        ShipmentDetail.receiver.name ||
                        ShipmentDetail.receiver.customerUser?.name ||
                        "N/A"
                      ) : (
                        <OpenInNewTab
                          label={
                            ShipmentDetail.receiver.name ||
                            ShipmentDetail.receiver.customerUser?.name ||
                            "N/A"
                          }
                          link={`/app/crm/customerProfile/${ShipmentDetail.receiver._id}/detail`}
                        />
                      )
                    ) : (
                      "N/A"
                    )
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <Warehouse color="primary" />}
                  title="Source Branch"
                  subtitle={
                    ShipmentDetail?.sourceBranch &&
                      typeof ShipmentDetail.sourceBranch === "object" ? (
                      canGoToLink ? (
                        ShipmentDetail.sourceBranch.name || "N/A"
                      ) : (
                        <OpenInNewTab
                          label={ShipmentDetail.sourceBranch.name || "N/A"}
                          link={`/app/crm/branch/${ShipmentDetail.sourceBranch._id}/detail`}
                        />
                      )
                    ) : (
                      "N/A"
                    )
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <Warehouse color="primary" />}
                  title="Destination Branch"
                  subtitle={
                    ShipmentDetail?.destinationBranch &&
                      typeof ShipmentDetail.destinationBranch === "object" ? (
                      canGoToLink ? (
                        ShipmentDetail.destinationBranch.name || "N/A"
                      ) : (
                        <OpenInNewTab
                          label={ShipmentDetail.destinationBranch.name || "N/A"}
                          link={`/app/crm/branch/${ShipmentDetail.destinationBranch._id}/detail`}
                        />
                      )
                    ) : (
                      "N/A"
                    )
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <HouseOutlined color="primary" />}
                  title="Receiver Address"
                  subtitle={ShipmentDetail?.receiver?.address || "N/A"}
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <PhoneAndroidOutlined color="primary" />}
                  title="Receiver Phone"
                  subtitle={ShipmentDetail?.receiver?.phone || "N/A"}
                />
              </Grid>
              <Grid item md={3}>
                <InformationTile
                  icon={() => <SourceOutlined color="primary" />}
                  title="Reference"
                  subtitle={ShipmentDetail?.reference}
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <PriorityHighOutlined color="primary" />}
                  title="Priority"
                  subtitle={ShipmentDetail?.priority?.name || "N/A"}
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <LocalShippingOutlined color="primary" />}
                  title="Shipment Delivery"
                  subtitle={ShipmentDetail?.shipmentDelivery || "N/A"}
                />
              </Grid>

              <Grid item xs={3}>
                <InformationTile
                  icon={() => <LocalShippingOutlined color="primary" />}
                  title="Delivery Type"
                  subtitle={ShipmentDetail?.deliveryTypes || "N/A"}
                />
              </Grid>
              {ShipmentDetail?.uploadedId?.length > 0 && (
                <Grid item xs={3}>
                  <InformationTile
                    icon={() => <AttachFile color="primary" />}
                    title="ID Proof"
                  >
                    <div className="flex flex-wrap gap-4">
                      {ShipmentDetail.uploadedId.map((file) => (
                        <a
                          key={file?._id}
                          href={file?.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "var(--primary)" }}
                        >
                          <FileIcon fileName={file?.name} fileUrl={file?.url} />
                        </a>
                      ))}
                    </div>
                  </InformationTile>
                </Grid>
              )}
              {ShipmentDetail?.weight && (
                <Grid item xs={3}>
                  <InformationTile
                    icon={() => <LineWeight color="primary" />}
                    title="Shipment Weight"
                    subtitle={`${ShipmentDetail.weight} kg`}
                  />
                </Grid>
              )}
              {ShipmentDetail?.unit?.value && ShipmentDetail?.unit?.unitType?.name && (
                <Grid item xs={3}>
                  <InformationTile
                    icon={() => <LineWeight color="primary" />}
                    title={
                      ShipmentDetail.unit.unitType.name.toLowerCase() === "weight"
                        ? "Shipment Weight"
                        : "Shipment Unit"
                    }
                    subtitle={
                      ShipmentDetail.unit.unitType.name.toLowerCase() === "weight"
                        ? `${ShipmentDetail.unit.value} kg`
                        : `${ShipmentDetail.unit.value} ${ShipmentDetail.unit.unitType.name}`
                    }
                  />
                </Grid>
              )}
              {ShipmentDetail?.dimensions.length > 0 && (
                <Grid item xs={3}>
                  <InformationTile
                    icon={() => <LocationOnOutlined color="primary" />}
                    title="Dimensions"
                    subtitle={`Length: ${ShipmentDetail?.dimensions?.length || 0
                      }m, Width: ${ShipmentDetail?.dimensions?.width || 0
                      }m, Height: ${ShipmentDetail?.dimensions?.height || 0}m`}
                  />
                </Grid>
              )}
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <AccessTime color="primary" />}
                  title="Shipment Pickup Time"
                  subtitle={
                    ShipmentDetail?.shipmentPickupTime
                      ? localizeDateTimeShort(
                        ShipmentDetail?.shipmentPickupTime
                      )
                      : "N/A"
                  }
                />
              </Grid>
              {ShipmentDetail?.shipmentDeliveryTime && (
                <Grid item xs={3}>
                  <InformationTile
                    icon={() => <AccessTime color="primary" />}
                    title="Shipment Delivery Time"
                    subtitle={
                      ShipmentDetail?.shipmentDeliveryTime
                        ? new Date(ShipmentDetail.shipmentDeliveryTime).toLocaleString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        })
                        : "N/A"
                    }
                  />
                </Grid>
              )}
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="In Warehouse ?"
                  subtitle={ShipmentDetail?.atWarehouse ? "YES" : "NO"}
                />
              </Grid>
              {/* <Grid item xs={3}>
                <InformationTile
                  icon={() => <Money color="primary" />}
                  title="Total Shipment Amount"
                  subtitle={`Rs. ${(
                    ShipmentDetail?.shipmentAmount || 0
                  ).toFixed(2)}`}
                />
              </Grid>
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <Money color="primary" />}
                  title="Remaining Shipment Amount"
                  subtitle={`Rs. ${(
                    ShipmentDetail?.remainingShipmentAmount || 0
                  ).toFixed(2)}`}
                />
              </Grid> */}
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="Status"
                >
                  <Status isActive={ShipmentDetail?.isActive} />
                </InformationTile>
              </Grid>

              {(ShipmentDetail?.status !== "Pending") && (<Grid item xs={3}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="Consolidation Status"
                >
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color={ShipmentDetail?.isConsolidated ? "green" : "orange"}
                  >
                    {ShipmentDetail?.isConsolidated
                      ? "Consolidated"
                      : "Deconsolidated"}
                  </Typography>
                </InformationTile>
              </Grid>)}

              <Grid item xs={3}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="Hold Status"
                >
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color={ShipmentDetail?.isOnHold ? "orange" : "green"}
                  >
                    {ShipmentDetail?.isOnHold ? "On Hold" : "Not On Hold"}
                  </Typography>
                  {ShipmentDetail?.isOnHold && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {ShipmentDetail?.holdStartedAt && (
                        <>Started: {localizeDateTimeShort(ShipmentDetail.holdStartedAt)}<br /></>
                      )}
                      {ShipmentDetail?.holdUntil && (
                        <>Auto-release: {localizeDateTimeShort(ShipmentDetail.holdUntil)}<br /></>
                      )}
                      Remaining: <strong>{formatTimeRemaining(ShipmentDetail?.holdUntil)}</strong>
                    </Typography>
                  )}
                </InformationTile>
              </Grid>
              {ShipmentDetail?.IsReturning && (
                <Grid item xs={3}>
                  <InformationTile
                    icon={() => <InfoOutlined color="primary" />}
                    title="isRetuning"
                  >
                    <Typography variant="body2" fontWeight={500} color="red">
                      Returning
                    </Typography>
                  </InformationTile>
                </Grid>
              )}
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <SecurityIcon color="primary" />}
                  title="Delivery Security"
                  subtitle={
                    ShipmentDetail?.deliverySecurity ? (
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="start"
                        mt={1}
                      >
                        <Chip
                          label={ShipmentDetail.deliverySecurity}
                          sx={{
                            backgroundColor: getSecurityStyle(
                              ShipmentDetail.deliverySecurity
                            )?.backgroundColor,
                            color: getSecurityStyle(
                              ShipmentDetail.deliverySecurity
                            )?.textColor,
                            fontWeight: "bold",
                            height: "17px",
                            borderRadius: "12px",
                            width: "auto",
                            padding: "0",
                            border: "none",
                            "& .MuiChip-label": {
                              fontSize: "0.6rem",
                              padding: "3px 5px",
                              lineHeight: "14px",
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      "N/A"
                    )
                  }
                />
              </Grid>
              {/* <Grid item xs={3}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="Payment Status"
                  subtitle={
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="start"
                      mt={1}
                    >
                      <Chip
                        label={ShipmentDetail?.paymentStatus || "Unknown"}
                        sx={{
                          backgroundColor: getStatusStyle(
                            ShipmentDetail?.paymentStatus
                          )?.backgroundColor,
                          color: getStatusStyle(ShipmentDetail?.paymentStatus)
                            ?.textColor,
                          fontWeight: "bold",
                          height: "17px",
                          borderRadius: "12px",
                          width: "auto",
                          padding: "0",
                          border: "none",
                          "& .MuiChip-label": {
                            fontSize: "0.6rem",
                            padding: "3px 5px",
                            lineHeight: "14px",
                          },
                        }}
                      />
                    </Box>
                  }
                />
              </Grid> */}
              {/* <Grid item xs={3}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="Payment Collection Status"
                  subtitle={
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="start"
                      mt={1}
                    >
                      <Chip
                        label={
                          ShipmentDetail?.paymentCollectionStatus || "Unknown"
                        }
                        sx={{
                          backgroundColor: getStatusStyle(
                            ShipmentDetail?.paymentCollectionStatus
                          )?.backgroundColor,
                          color: getStatusStyle(
                            ShipmentDetail?.paymentCollectionStatus
                          )?.textColor,
                          fontWeight: "bold",
                          height: "17px",
                          borderRadius: "12px",
                          width: "auto",
                          padding: "0",
                          border: "none",
                          "& .MuiChip-label": {
                            fontSize: "0.6rem",
                            padding: "3px 5px",
                            lineHeight: "14px",
                          },
                        }}
                      />
                    </Box>
                  }
                />
              </Grid> */}
              <Grid item xs={3}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="Shipment Status"
                  subtitle={
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="start"
                      mt={1}
                    >
                      <Chip
                        label={ShipmentDetail?.status || "Unknown"}
                        sx={{
                          backgroundColor: getStatusStyle(
                            ShipmentDetail?.status
                          )?.backgroundColor,
                          color: getStatusStyle(ShipmentDetail?.status)
                            ?.textColor,
                          fontWeight: "bold",
                          height: "17px",
                          borderRadius: "12px",
                          width: "auto",
                          padding: "0",
                          border: "none",
                          "& .MuiChip-label": {
                            fontSize: "0.6rem",
                            padding: "3px 5px",
                            lineHeight: "14px",
                          },
                        }}
                      />
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <InformationTile
                  icon={() => <ProductionQuantityLimits color="primary" />}
                  title="Product Description"
                  subtitle={ShipmentDetail?.productDescription || "N/A"}
                />
              </Grid>
              <Grid item xs={6}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="Remarks"
                  subtitle={ShipmentDetail?.remarks || "N/A"}
                />
              </Grid>
              <Grid item xs={6}>
                <InformationTile
                  icon={() => <InfoOutlined color="primary" />}
                  title="Instruction"
                  subtitle={ShipmentDetail?.instruction || "N/A"}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item md={2}>
          <Box
            sx={{
              textAlign: "center",
              marginY: 2,
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 2,
              }}
            >
              <QRCode value={ShipmentDetail?.shipmentId || "N/A"} size={120} />
            </Box>
            {/* Show on hold image if isOnHold is true */}
            {ShipmentDetail?.isOnHold && (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <img
                  src={onHoldImage}
                  alt="On Hold"
                  style={{
                    width: "150px",
                    borderRadius: "8px",
                  }}
                />
              </Box>
            )}
            {/* Show return image if IsReturning is true */}
            {!ShipmentDetail?.isOnHold && ShipmentDetail?.IsReturning && (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <img
                  src={returnImage}
                  alt="Returning"
                  style={{
                    width: "150px",
                    borderRadius: "8px",
                  }}
                />
              </Box>
            )}
            {/* Show other status images if not on hold and not returning */}
            {!ShipmentDetail?.isOnHold &&
              !ShipmentDetail?.IsReturning &&
              statusImages[ShipmentDetail?.status] && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <img
                    src={statusImages[ShipmentDetail?.status]}
                    alt={ShipmentDetail?.status}
                    style={{
                      width: "150px",
                      borderRadius: "8px",
                    }}
                  />
                </Box>
              )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default ShipmentBasicDetail;